import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { additionalIncomeReportAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import './AdditionalIncomeReportCreate.css';

const AdditionalIncomeReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadReport();
  }, [id]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await additionalIncomeReportAPI.getById(id);
      const data = response.data;
      setReport({
        ...data,
        reportDate: data.reportDate ? new Date(data.reportDate).toISOString().split('T')[0] : '',
        amount: data.amount || '',
        notes: data.notes || '',
        isActive: data.isActive ? 'true' : 'false',
      });
    } catch (err) {
      setErrorMessage(t('additionalIncomeReport.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">{t('additionalIncomeReport.view.messages.notFound')}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header-actions">
          <button className="btn-back" onClick={() => navigate('/additional-income-report')}>
            <i className="fas fa-arrow-left"></i> {t('additionalIncomeReport.view.backToList')}
          </button>
          <button className="btn-edit" onClick={() => navigate(`/additional-income-report/edit/${id}`)}>
            <i className="fas fa-edit"></i> {t('additionalIncomeReport.view.edit')}
          </button>
        </div>

      <div className="form-container">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('additionalIncomeReport.view.fields.date')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="date"
                className="form-input"
                value={report.reportDate}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-dollar-sign"></i> {t('additionalIncomeReport.view.fields.amount')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-dollar-sign input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={parseFloat(report.amount).toFixed(2)}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-check-circle"></i> {t('additionalIncomeReport.view.fields.isActive')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-check-circle input-icon"></i>
              <select
                className="form-input"
                value={report.isActive}
                disabled
              >
                <option value="true">{t('common.yes')}</option>
                <option value="false">{t('common.no')}</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-sticky-note"></i> {t('additionalIncomeReport.view.fields.notes')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-sticky-note input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={report.notes || ''}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-globe"></i> {t('additionalIncomeReport.view.fields.incomeDescriptionEng')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-globe input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={report.incomeDescriptionEng}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-language"></i> {t('additionalIncomeReport.view.fields.incomeDescriptionAr')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-language input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={report.incomeDescriptionAr}
                readOnly
                disabled
              />
          </div>
        </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-globe"></i> {t('additionalIncomeReport.view.fields.recipientNameEng')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-globe input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={report.recipientNameEng}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-language"></i> {t('additionalIncomeReport.view.fields.recipientNameAr')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-language input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={report.recipientNameAr}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('additionalIncomeReport.view.fields.createdAt')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={report.createdAt ? new Date(report.createdAt).toLocaleString() : ''}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default AdditionalIncomeReportView;

